import { memo, useContext, useState } from 'react';

import PropTypes from 'prop-types';

import { cn } from '@/shadcn/lib/utils';

import { getByPath } from '@/utils/widgets';
import {
  enhanceSelectableView,
  withSelectableContext
} from './contexts/selectableItems';
import { getView } from './views';
import { WidgetRenderContext } from './widgetRenderContext';
import { InlineStyleWrapper } from './wrappers/inlineStyle/inlineStyle.wrapper';
import { LinkWrapper } from './wrappers/link';
import { ModalWrapper } from './wrappers/modal/modal.wrapper';
import { TooltipWrapper } from './wrappers/tooltip/tooltip.wrapper';
import { TrayWrapper } from './wrappers/tray/tray.wrapper';

const hasWrapper = (data, wrapper) => {
  if (!wrapper) return false;
  const { conditionPath } = wrapper;
  return !conditionPath || !!getByPath(data, conditionPath);
};
const getViewComponent = (view, data, update) => {
  let ViewComponent = getView(view).component;
  if (view.selectable || view.selectableItems) {
    ViewComponent = withSelectableContext(ViewComponent);
  }
  return <ViewComponent data={data} update={update} view={view} />;
};
const getHasNoPadding = view => {
  // some views are better displayed without padding
  const viewType = view?.type;
  switch (viewType) {
    case 'empty':
      return true;
    default:
  }

  return !!view?.editor;
};

/* widget component */
export const Widget = memo(
  ({
    widget,
    data,
    update = null,
    className = '',
    disableWrappers = false,
    renderContext
  }) => {
    if (!widget) return null;

    const inheritedRenderContext = useContext(WidgetRenderContext);
    const resolvedRenderContext = renderContext ?? inheritedRenderContext;

    // get view
    const view = widget.view?.selectableItems
      ? enhanceSelectableView(widget.view)
      : widget.view;

    // wrappers
    const { link, modal, tooltip, tray, inlineStyle } = widget;

    // provide wrapper sub-components with a way to tell the widget to keep focused
    const [isFocus, setFocus] = useState(false);

    // disable wrappers when specified (eg when editing layout)
    const hasLink = !disableWrappers && hasWrapper(data, link);
    const hasModal = !disableWrappers && hasWrapper(data, modal);
    const hasTooltip = !disableWrappers && hasWrapper(data, tooltip);
    const hasTray = !disableWrappers && hasWrapper(data, tray);
    const hasInlineStyle = !disableWrappers && hasWrapper(data, inlineStyle);
    const isHoverable = hasLink || hasModal || hasTray;
    const hasNoPadding = getHasNoPadding(view);

    // get the view component, ie what is displayed inside the widget
    const viewComponent = getViewComponent(view || {}, data, update);

    let widgetComponent = (
      <div
        className={cn(className, 'size-full')}
        data-is-focus={isFocus} // the widget is styled 'hoverable' if there is at least one wrapper
        data-is-hoverable={isHoverable}
        data-no-padding={hasNoPadding}
      >
        {viewComponent}
      </div>
    );

    // wrap with link
    if (hasLink) {
      const { rootUrl, urlPath, openInNewTab } = link;
      const urlData = getByPath(data, urlPath);
      widgetComponent = (
        <LinkWrapper openInNewTab={openInNewTab} url={`${rootUrl}${urlData}`}>
          {widgetComponent}
        </LinkWrapper>
      );
    }

    // wrap with modal
    if (hasModal) {
      const {
        title: modalTitle = {},
        body: modalBody = {},
        large,
        fullscreen
      } = modal;

      // Modal title
      // let the user choose between a simple text title or a more complete widget setup
      const modalTitleWidget =
        typeof modalTitle === 'object' ? (
          <Widget
            data={data}
            renderContext="default"
            update={update}
            widget={modalTitle}
          />
        ) : (
          <div>{modalTitle}</div>
        );

      // Modal body
      const modalBodyWidget = (
        <Widget
          data={data}
          renderContext="default"
          update={update}
          widget={modalBody}
        />
      );

      widgetComponent = (
        <ModalWrapper
          body={modalBodyWidget}
          fullscreen={fullscreen}
          large={large}
          setClientFocus={setFocus}
          title={modalTitleWidget}
        >
          {widgetComponent}
        </ModalWrapper>
      );
    }

    // wrap with tooltip
    if (hasTooltip) {
      const {
        title: tooltipTitle,
        body: tooltipBody = {},
        position,
        delay,
        withArrow
      } = tooltip;

      const getContent = item => {
        if (!item) return null;
        return typeof item === 'object' ? (
          <Widget
            data={data}
            renderContext="default"
            update={update}
            widget={item}
          />
        ) : (
          <div>{item}</div>
        );
      };
      widgetComponent = (
        <TooltipWrapper
          body={getContent(tooltipBody)}
          delay={delay}
          position={position}
          title={getContent(tooltipTitle)}
          withArrow={withArrow}
        >
          {widgetComponent}
        </TooltipWrapper>
      );
    }

    // wrap with tray
    if (hasTray) {
      const {
        title: trayTitle = {},
        body: trayBody = {},
        width,
        collapsedWidth,
        resizable,
        minWidth
      } = tray;

      // Tray title
      // let the user choose between a simple text title or a more complete widget setup
      const trayTitleWidget =
        typeof trayTitle === 'object' ? (
          <Widget data={data} renderContext="default" widget={trayTitle} />
        ) : (
          trayTitle
        );

      // Tray body
      const trayBodyWidget = (
        <Widget
          data={data}
          renderContext="default"
          update={update}
          widget={trayBody}
        />
      );

      widgetComponent = (
        <TrayWrapper
          body={trayBodyWidget}
          collapsedWidth={collapsedWidth}
          minWidth={minWidth}
          resizable={resizable}
          setClientFocus={setFocus}
          title={trayTitleWidget}
          width={width}
        >
          {widgetComponent}
        </TrayWrapper>
      );
    }

    // wrap with inline style
    if (hasInlineStyle) {
      widgetComponent = (
        <InlineStyleWrapper data={data} rules={inlineStyle}>
          {widgetComponent}
        </InlineStyleWrapper>
      );
    }

    return (
      <WidgetRenderContext.Provider value={resolvedRenderContext}>
        {widgetComponent}
      </WidgetRenderContext.Provider>
    );
  }
);
Widget.propTypes = {
  widget: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  update: PropTypes.object,
  className: PropTypes.string,
  disableWrappers: PropTypes.bool,
  renderContext: PropTypes.oneOf(['default', 'table-cell'])
};

/* interfaces */
const getViewConfig = widget => widget.view || {};
// note: so far only the view provides access to the default sort and filter configs
// later, views could delegate the choice to wrappers as well if they can't decide (eg icons...)
export const getSortConfig = widget => {
  const view = getViewConfig(widget);
  return getView(view).getSortConfig(view);
};

/**
 * @returns {FilterConfig}
 */
export const getFilterConfig = widget => {
  const view = getViewConfig(widget);
  return getView(view).getFilterConfig(view);
};
export const getDefaultWidth = widget => {
  const view = getViewConfig(widget);
  return getView(view).getDefaultWidth(view);
};
export const getStringifyFunction = (widget, workflowContext) => {
  const view = getViewConfig(widget);
  const viewStringifyFunction = getView(view).getStringifyFunction;
  // if the view does not specify any stringify function just output empty string
  return viewStringifyFunction
    ? viewStringifyFunction(view, workflowContext)
    : () => '';
};
export const getEditedPath = widget => {
  // get the path of the data linked to the editor if configured
  const view = getViewConfig(widget);
  const viewEditedPathFunction = getView(view).getEditedPath;
  return viewEditedPathFunction ? viewEditedPathFunction(view) : null;
};
export const getAggregationConfig = widget => {
  let view = getViewConfig(widget);
  const { aggregation } = widget;
  if (aggregation?.aggregationValue) {
    const { aggregationKey, aggregationValue, aggregationFunction } =
      aggregation;
    view = {
      ...view,
      aggregationKey,
      aggregationValue,
      aggregationFunction
    };
  }
  const getAggregationConfigFn = getView(view).getAggregationConfig;
  if (!getAggregationConfigFn) return null; // by default no aggregation
  return getAggregationConfigFn(view);
};
